import { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AmountUtils, cn, CurrencyUtils, PaletteUtils } from '@shared/utils';
import { type AccountResponse, AccountType } from '@shared/api';
import { Icon, Modal, NoData, Text } from '@shared/widgets';
import DollarIcon from '@assets/icons/dollar.svg';
import SwapIcon from '@assets/icons/swap.svg';

interface SelectAccountProps {
  className?: string;
  label?: React.ReactNode;
  loading?: boolean;
  type?: AccountType;
  currentAccount?: AccountResponse | null;
  accounts: AccountResponse[];
  onChange?: (value: AccountResponse) => void;
  balanceFieldName?: string;
}

export const SelectAccount = (props: SelectAccountProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const onSelect = useCallback(
    (value: AccountResponse) => {
      props.onChange && props.onChange(value);
      setOpen(false);
    },
    [props.onChange],
  );

  const title = useMemo(() => props.label ?? t('placeholders.selectAccount'), [props.label]);
  const accountsPaymentList = useMemo(
    () => (props.type ? props.accounts.filter((account) => account.type === props.type) : props.accounts),
    [props.type, props.accounts],
  );
  return (
    <>
      <div className={cn('flex flex-col gap-3', props.className)}>
        <Text className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{title}</Text>
        <AccountListItem account={props.currentAccount} balanceFieldName={props.balanceFieldName} isShowEndIcon onClick={() => setOpen(true)} />
      </div>
      <Modal isOpen={open} title={title as string} onClose={() => setOpen(false)}>
        {accountsPaymentList.length < 1 ? (
          <NoData />
        ) : (
          <div className='flex flex-col gap-2'>
            {accountsPaymentList.map((account, idx) => (
              <AccountListItem
                key={idx}
                className={cn('w-[456px]', PaletteUtils.isInsigniaPalette && 'dark:bg-slate', 'dark:hover:bg-slate/70')}
                titleClassName='text-xl'
                account={account}
                balanceFieldName={props.balanceFieldName}
                isShowEndIcon={false}
                onClick={() => onSelect(account)}
              />
            ))}
          </div>
        )}
      </Modal>
    </>
  );
};

interface AccountListItemProps {
  className?: string;
  titleClassName?: string;
  balanceFieldName?: string;
  account?: AccountResponse | null;
  isShowEndIcon?: boolean;
  onClick: () => void;
}

const AccountListItem = (props: AccountListItemProps) => {
  const { t } = useTranslation();

  const currencySymbol = useMemo(() => CurrencyUtils.getSymbolByCode(props.account?.currency), [props.account?.currency]);
  return (
    <div
      className={cn(
        'flex cursor-pointer flex-row p-3 transition-colors duration-200',
        PaletteUtils.isInsigniaPalette && ['bg-ice font-[Prata] dark:bg-eerie-black', 'hover:bg-ghost-white dark:hover:bg-transport-black'],
        PaletteUtils.isVcfcPalette && ['bg-ghost-white dark:bg-light/5', 'hover:bg-gainsborough dark:hover:bg-light/20'],
        props.className,
      )}
      onClick={props.onClick}
    >
      <div
        className={cn(
          'flex h-[42px] w-[68px] items-center justify-center rounded-[5px] bg-soft-black text-2xl',
          PaletteUtils.isInsigniaPalette && 'text-primary',
          PaletteUtils.isVcfcPalette && 'text-white',
        )}
      >
        {currencySymbol ? currencySymbol : <Icon icon={<DollarIcon />} color='inherit' />}
      </div>
      <div className='my-auto ml-3 flex flex-col justify-between'>
        {props.account ? (
          <>
            <Text className={props.titleClassName}>
              {props.balanceFieldName
                ? AmountUtils.transformToAmountWithCurrency(props?.account?.[props.balanceFieldName as keyof AccountResponse] as number)
                : AmountUtils.transformToAmountWithCurrency(props.account.balance)}
              {currencySymbol}
            </Text>
            <Text weight='semibold' size='xs' color='secondary'>
              {props.account.productName} {props.account.accountNumber}
            </Text>
          </>
        ) : (
          <Text>{t('placeholders.selectAccount')}...</Text>
        )}
      </div>
      {props.isShowEndIcon && (
        <div className={cn('my-auto ml-auto flex items-center gap-3', PaletteUtils.isVcfcPalette && 'ml-6')}>
          {props.account && (
            <div
              className={cn(
                'flex h-7 min-w-16 items-center justify-center rounded border border-primary p-2',
                PaletteUtils.isVcfcPalette && 'h-[34px] bg-white dark:border-white dark:bg-light/5',
              )}
            >
              <Text className='mr-2' size='xs' weight='semibold' color='primary'>
                {CurrencyUtils.getSymbolByCode(props.account?.currency)}
              </Text>
              <Text size='xs' weight='semibold' uppercase color='primary'>
                {props.account.mainAccId === null ? t('accountType.type.main') : t('accountType.type.sub')}
              </Text>
            </div>
          )}
          <Icon icon={<SwapIcon width='24' height='24' />} className='rotate-90' />
        </div>
      )}
    </div>
  );
};
